import { Device, DeviceSearch, Geotab, Group, GroupFilterCondition } from "@fairfleet/geotab";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";
import createGroupFilter, { PartialGroupFilterCondition } from "@/utilities/createGroupFilter";
import { DeviceCapError, deviceCapIsFinal } from "@/utilities/error/DeviceCapError";

export interface GroupScope {
  groups?: Group[];
  groupFilterCondition?: GroupFilterCondition;
}

/**
 * The DeviceSearch members this module sends. The generated type marks every member as required
 * and wants a full GroupFilterCondition, so the group condition is the partial one actually sent.
 */
export type DeviceSearchFilter = Omit<Partial<DeviceSearch>, "groupFilterCondition"> & {
  groupFilterCondition?: PartialGroupFilterCondition;
};

/**
 * Number of devices a DeviceSearch matches, in one cheap call. Throws {@link DeviceCapError} when
 * it reaches the Device cap: a search for them would come back clamped. Archived devices count
 * unless the search carries `fromDate`, which `GetCountOf` honours (live 2026-10-14: all 9078,
 * with `{ fromDate: now }` 7341).
 *
 * `GetCountOf` with a `search` is not in the Geotab reference (it lists only `typeName`), but it
 * honours a DeviceSearch: verified live on 2026-10-14 (whole database 9075, one group 33, the
 * same as a `Get` with that search). Only those shapes are verified, so callers check
 * {@link isCountVerifiedSearch} first.
 */
async function checkDeviceSearchCap(
  geotabClient: Geotab,
  search: DeviceSearchFilter,
  signal?: AbortSignal,
): Promise<number> {
  const count = (await geotabClient.call("GetCountOf", { typeName: "Device", search }, signal)) as number;

  if (count >= RESULTS_LIMIT_DEVICE) {
    throw new DeviceCapError(RESULTS_LIMIT_DEVICE);
  }
  return count;
}

/**
 * Ids of every device (archived included) in the add-in's group filter. Checks the cap first
 * ({@link checkDeviceSearchCap}) so a too-wide filter fails in seconds instead of after loading
 * 5000 devices, then loads only the id of each. For a filter shape the count is not verified for,
 * it skips the count and relies on the full-page check alone: slower to fail, but a count that
 * ignored the filter could never block a filter that is actually small.
 */
export async function fetchGroupDeviceIds(
  geotabClient: Geotab,
  scope: GroupScope,
  signal?: AbortSignal,
): Promise<string[]> {
  const search = groupSearch(scope);
  if (isCountVerifiedSearch(search)) {
    await checkDeviceSearchCap(geotabClient, search, signal);
  }
  return fetchDeviceSearchIds(geotabClient, search, signal);
}

/** Ids of the devices a DeviceSearch matches, id only; a full page throws {@link DeviceCapError}. */
export async function fetchDeviceSearchIds(
  geotabClient: Geotab,
  search: DeviceSearchFilter,
  signal?: AbortSignal,
): Promise<string[]> {
  const devices = (await geotabClient.call(
    "Get",
    {
      typeName: "Device",
      search,
      // Top-level, beside `search`: nested inside the search it is ignored.
      propertySelector: { fields: ["id"], isIncluded: true },
      resultsLimit: RESULTS_LIMIT_DEVICE,
    },
    signal,
  )) as Pick<Device, "id">[] | null;

  // Vehicles can be added after a count; a full page is still incomplete.
  if ((devices?.length ?? 0) >= RESULTS_LIMIT_DEVICE) {
    throw new DeviceCapError(RESULTS_LIMIT_DEVICE);
  }
  return (devices ?? []).map(d => d.id);
}

/**
 * Whether `GetCountOf` is live-verified to honour this search's group filter: none (the whole
 * database) or a single `{ groupId }`. Not verified: the multi-group OR that
 * {@link createGroupFilter} builds for more than one group, and nested advanced filters. Were
 * those ignored, the whole-database count would come back as a false cap error.
 */
function isCountVerifiedSearch(search: DeviceSearchFilter): boolean {
  const condition = search.groupFilterCondition;
  if (condition == null) return true;
  return typeof condition.groupId === "string" && condition.groupFilterConditions == null;
}

function groupSearch({ groups, groupFilterCondition }: GroupScope): DeviceSearchFilter {
  return createGroupFilter(groups, groupFilterCondition, {});
}

/**
 * {@link fetchGroupDeviceIds} for the current group filter, cached per filter. A cap error is
 * final for that filter: no retry, no refetch on focus, reconnect or mount, so the error stays
 * visible instead of flipping back to loading. Only a different filter asks again. Any other error
 * is retried as usual, including on focus, reconnect and mount.
 */
export function useGroupDeviceIds({ enabled = true }: { enabled?: boolean } = {}) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();
  const queryClient = useQueryClient();
  const queryKey = [
    "groupDeviceIds",
    activeSessionId,
    { groupIds: groups?.map(g => g.id), groupFilterCondition },
  ];

  return useQuery({
    queryKey,
    enabled,
    queryFn: ({ signal }) =>
      fetchGroupDeviceIds(geotabClient, { groups, groupFilterCondition }, signal),
    ...deviceCapIsFinal(queryClient, queryKey),
  });
}
